/**
 * Dev Environments store module — `/my/dev-environments` (P8).
 *
 * Holds (a) the section CAPABILITY resolved by a one-shot probe against the
 * cluster-api P6 API and (b) the org-scoped list of environments plus their
 * normalised rows. The nav entry reads the capability; the views read the rows.
 *
 * Namespaced, NOT persisted — the vuex-persist reducer in store/index.js
 * whitelists only `auth`, so nothing here survives a reload (by design: the
 * capability must be re-probed with the current token/org).
 *
 * Capability probe — `probe()` calls `list({ include_terminal: 1 })` once and
 * classifies the outcome with `classifyGate`:
 *   available       → 2xx — the list doubles as the first load (cached)
 *   disabled        → 503 dev_environments_disabled (feature flag off) → nav hidden
 *   forbidden       → 403 bare forbidden → nav shown with a lock (discoverable)
 *   unavailable     → 503 service_unavailable / network — treated as transient
 *   unauthenticated → 401
 *   error           → anything else
 * In-flight probes are de-duplicated (module-level promise) so the NavBar
 * watcher and a view mounting at the same time issue ONE request.
 *
 * List poll — `startPoll()` chains setTimeout ticks ONLY while some row is
 * transitional (`needsListPoll`), backing off 3 s → ×1.5 → 15 s cap
 * (`nextPollDelay`). Any mutation that makes a row transitional (create /
 * start / stop) resets the backoff and (re)arms the chain. It polls the LIST
 * endpoint only — `GET /<id>/workspace` bumps `last_active_at` and must never
 * be polled.
 *
 * Git (developer loop Stage A) — `gitStatus` / `gitDiff` / `gitBusy` /
 * `gitError` are keyed by env id. They are loaded by the Changes card on
 * mount, on an explicit Refresh and after a push — NEVER from the 5 s detail
 * poll (each call is a pod exec into the workspace). `loadGitStatus` never
 * throws to the view: it parks a `gitErrorCopy()` on `gitError[id]`.
 *
 * Release (developer loop Stage B) — `release` / `releaseBusy` / `releaseError`
 * by env id over `GET /<id>/release/status`, which is built from GitLab + the
 * cluster + the record and NEVER touches the workspace, so it IS polled:
 * `startReleasePoll` chains 6 s → ×1.5 → 15 s (`releasePollDelay`) until
 * BOTH `deployed.state` is terminal AND the pipeline itself has settled
 * (`releasePollShouldStop` — the image swaps before GitLab flips the pipeline
 * to `success`, so "deployed" alone is not the end), a non-transient error
 * lands, or 10 min pass. `releaseAdopt` / `releaseFinalize` refresh the
 * status afterwards and re-arm the poll while a build is in flight.
 *
 * Connection info — `connectionInfo` / `connectionInfoBusy` /
 * `connectionInfoError` by env id over `GET /<id>/connection-info` (the APP's
 * connection info from the installed-app builder; never the workspace). Loaded
 * on demand when the App card's modal opens and on its Refresh — never polled.
 * `loadConnectionInfo` never throws to the view: it parks an `errorCopy()` on
 * `connectionInfoError[id]`. The payload may carry credentials: it lives in
 * memory only (this module is not persisted) and is never logged.
 *
 * Credential values are masked by cluster-api (2.25.43), not by us — the
 * payload arrives with stubs and a `secret_masking` report. `loadConnectionInfo`
 * takes `{ id, reveal }`: a reveal is a SEPARATE, audited request the server
 * grants only to a `secret:viewer`, so a failed reveal never disturbs the
 * masked payload already on screen — its copy parks on
 * `connectionInfoRevealError[id]` instead.
 */
import { devEnvironmentsApi } from '@/services/dev-environments-api'
import { classifyGate, apiErrorCode } from '@/utils/dev-env-api-core'
import {
  toEnvRow,
  needsListPoll,
  nextPollDelay,
  errorCopy,
  gitErrorCopy
} from '@/utils/dev-env-shape'
import {
  releaseErrorCopy,
  releasePollDelay,
  releasePollShouldStop,
  RELEASE_POLL_MAX_MS
} from '@/utils/dev-env-shape'
import { revealErrorCopy } from '@/utils/secret-masking'

// Release poll chains live outside reactive state (timer handles + the
// per-env start stamp are bookkeeping, not view data).
const releasePolls = {} // { id: { handle, delay, startedAt, sha } }

// In-flight probe de-dupe. Lives outside reactive state on purpose — a Promise
// in Vuex state would be frozen/observed for nothing. `probeGen` is bumped by
// RESET so a probe started for the previous org/token cannot land its result
// after the state was cleared (org switch → reset → probe).
let inflightProbe = null
let probeGen = 0

const state = () => ({
  capability: 'unknown', // unknown|available|disabled|forbidden|unavailable|unauthenticated|error
  probing: false,
  probedAt: null,

  envs: [],        // raw public docs from the API
  rows: [],        // toEnvRow(env) projections (kept in sync by SET_ENVS/UPSERT/REMOVE)
  listLoading: false,
  listError: null, // errorCopy() shape | null
  loaded: false,

  pollHandle: null,
  pollDelay: 0,

  busy: {},        // { `${id}:${action}`: true }

  // Developer loop Stage A — by env id, never polled.
  gitStatus: {},   // { id: <GET /git/status payload> }
  gitDiff: {},     // { id: { path, diff, stat, truncated } }
  gitBusy: {},     // { id: 'status'|'diff'|'push' }
  gitError: {},    // { id: gitErrorCopy() shape }

  // Developer loop Stage B — by env id, polled while a release is in flight.
  release: {},       // { id: <GET /release/status payload> }
  releaseBusy: {},   // { id: 'status'|'adopt'|'finalize' }
  releaseError: {},  // { id: releaseErrorCopy() shape }
  releasePolling: {}, // { id: true } while a poll chain is armed

  // The APP's connection info — by env id, on demand, never polled.
  connectionInfo: {},      // { id: { info: <builder payload verbatim>, meta, loadedAt } }
  connectionInfoBusy: {},  // { id: true }
  connectionInfoError: {},      // { id: errorCopy() shape }
  connectionInfoRevealError: {} // { id: revealErrorCopy() shape } — a refused ?reveal=1
})

function rowsFrom(envs) {
  return (Array.isArray(envs) ? envs : []).map((e) => toEnvRow(e))
}

function envId(env) {
  if (!env || typeof env !== 'object') return null
  return env.id != null ? String(env.id) : env._id != null ? String(env._id) : null
}

const getters = {
  isAvailable: (s) => s.capability === 'available',
  isForbidden: (s) => s.capability === 'forbidden',
  isDisabled: (s) => s.capability === 'disabled',
  isUnavailable: (s) => s.capability === 'unavailable',
  isUnauthenticated: (s) => s.capability === 'unauthenticated',
  /** Nav entry is shown when the feature exists for this platform — even when
   *  the caller lacks the grant (locked), so the permission is discoverable. */
  navVisible: (s) => s.capability === 'available' || s.capability === 'forbidden',
  rowById: (s) => (id) => s.rows.find((r) => r.id === String(id)) || null,
  envById: (s) => (id) => s.envs.find((e) => envId(e) === String(id)) || null,
  isBusy: (s) => (id, action) => Boolean(s.busy[`${id}:${action}`]),
  activeCount: (s) => s.rows.filter((r) => r.status === 'active').length,
  transitionalCount: (s) => s.rows.filter((r) => r.isTransitional).length,

  gitStatusFor: (s) => (id) => s.gitStatus[String(id)] || null,
  gitDiffFor: (s) => (id) => s.gitDiff[String(id)] || null,
  gitBusyFor: (s) => (id) => s.gitBusy[String(id)] || null,
  gitErrorFor: (s) => (id) => s.gitError[String(id)] || null,

  releaseFor: (s) => (id) => s.release[String(id)] || null,
  releaseBusyFor: (s) => (id) => s.releaseBusy[String(id)] || null,
  releaseErrorFor: (s) => (id) => s.releaseError[String(id)] || null,
  releasePollingFor: (s) => (id) => Boolean(s.releasePolling[String(id)]),

  connectionInfoFor: (s) => (id) => s.connectionInfo[String(id)] || null,
  connectionInfoBusyFor: (s) => (id) => Boolean(s.connectionInfoBusy[String(id)]),
  connectionInfoErrorFor: (s) => (id) => s.connectionInfoError[String(id)] || null,
  connectionInfoRevealErrorFor: (s) => (id) => s.connectionInfoRevealError[String(id)] || null
}

/** Immutable set/delete on a `{ id: value }` map. */
function withKey(map, id, value) {
  const next = { ...map }
  if (value) next[String(id)] = value
  else delete next[String(id)]
  return next
}

const mutations = {
  SET_CAPABILITY(state, v) {
    state.capability = v || 'unknown'
    state.probedAt = Date.now()
  },
  SET_PROBING(state, v) { state.probing = Boolean(v) },

  SET_LIST_LOADING(state, v) { state.listLoading = Boolean(v) },
  SET_LIST_ERROR(state, v) { state.listError = v || null },
  SET_LOADED(state, v) { state.loaded = Boolean(v) },
  SET_ENVS(state, list) {
    state.envs = Array.isArray(list) ? list : []
    state.rows = rowsFrom(state.envs)
  },
  UPSERT_ENV(state, env) {
    const id = envId(env)
    if (!id) return
    const idx = state.envs.findIndex((e) => envId(e) === id)
    const next = [...state.envs]
    if (idx === -1) next.unshift(env)
    else next.splice(idx, 1, { ...next[idx], ...env })
    state.envs = next
    state.rows = rowsFrom(next)
  },
  REMOVE_ENV(state, id) {
    const key = String(id)
    state.envs = state.envs.filter((e) => envId(e) !== key)
    state.rows = rowsFrom(state.envs)
    const drop = (obj) => { const n = { ...obj }; delete n[key]; return n }
    state.gitStatus = drop(state.gitStatus)
    state.gitDiff = drop(state.gitDiff)
    state.gitBusy = drop(state.gitBusy)
    state.gitError = drop(state.gitError)
    state.release = drop(state.release)
    state.releaseBusy = drop(state.releaseBusy)
    state.releaseError = drop(state.releaseError)
    state.releasePolling = drop(state.releasePolling)
    state.connectionInfo = drop(state.connectionInfo)
    state.connectionInfoBusy = drop(state.connectionInfoBusy)
    state.connectionInfoError = drop(state.connectionInfoError)
    state.connectionInfoRevealError = drop(state.connectionInfoRevealError)
    clearReleasePoll(key)
  },

  SET_POLL_HANDLE(state, h) { state.pollHandle = h || null },
  SET_POLL_DELAY(state, ms) { state.pollDelay = ms || 0 },

  SET_BUSY(state, { id, action, busy }) {
    const key = `${id}:${action}`
    const next = { ...state.busy }
    if (busy) next[key] = true
    else delete next[key]
    state.busy = next
  },

  SET_GIT_STATUS(state, { id, status }) {
    const next = { ...state.gitStatus }
    if (status) next[String(id)] = status
    else delete next[String(id)]
    state.gitStatus = next
  },
  SET_GIT_DIFF(state, { id, diff }) {
    const next = { ...state.gitDiff }
    if (diff) next[String(id)] = diff
    else delete next[String(id)]
    state.gitDiff = next
  },
  SET_GIT_BUSY(state, { id, op }) {
    const next = { ...state.gitBusy }
    if (op) next[String(id)] = op
    else delete next[String(id)]
    state.gitBusy = next
  },
  SET_GIT_ERROR(state, { id, error }) {
    const next = { ...state.gitError }
    if (error) next[String(id)] = error
    else delete next[String(id)]
    state.gitError = next
  },

  SET_RELEASE(state, { id, status }) { state.release = withKey(state.release, id, status) },
  SET_RELEASE_BUSY(state, { id, op }) { state.releaseBusy = withKey(state.releaseBusy, id, op) },
  SET_RELEASE_ERROR(state, { id, error }) { state.releaseError = withKey(state.releaseError, id, error) },
  SET_RELEASE_POLLING(state, { id, on }) { state.releasePolling = withKey(state.releasePolling, id, on ? true : null) },

  SET_CONNECTION_INFO(state, { id, value }) { state.connectionInfo = withKey(state.connectionInfo, id, value) },
  SET_CONNECTION_INFO_BUSY(state, { id, busy }) { state.connectionInfoBusy = withKey(state.connectionInfoBusy, id, busy ? true : null) },
  SET_CONNECTION_INFO_ERROR(state, { id, error }) { state.connectionInfoError = withKey(state.connectionInfoError, id, error) },
  SET_CONNECTION_INFO_REVEAL_ERROR(state, { id, error }) { state.connectionInfoRevealError = withKey(state.connectionInfoRevealError, id, error) },

  RESET(state) {
    state.capability = 'unknown'
    state.probing = false
    state.probedAt = null
    state.envs = []
    state.rows = []
    state.listLoading = false
    state.listError = null
    state.loaded = false
    state.pollDelay = 0
    state.busy = {}
    state.gitStatus = {}
    state.gitDiff = {}
    state.gitBusy = {}
    state.gitError = {}
    state.release = {}
    state.releaseBusy = {}
    state.releaseError = {}
    state.releasePolling = {}
    state.connectionInfo = {}
    state.connectionInfoBusy = {}
    state.connectionInfoError = {}
    state.connectionInfoRevealError = {}
    // pollHandle is cleared by stopPoll (needs clearTimeout) — see actions.reset
  }
}

function clearReleasePoll(id) {
  const key = String(id)
  const p = releasePolls[key]
  if (p && p.handle) clearTimeout(p.handle)
  delete releasePolls[key]
}

/** Errors that mean "stop asking": the answer will not change by itself. */
function releaseErrorIsFinal(err) {
  const { status, code } = apiErrorCode(err)
  if (code === 'nothing_pushed' || code === 'invalid_sha') return true
  return status === 400 || status === 401 || status === 403 || status === 404 || status === 409
}

const actions = {
  /**
   * Resolve the section capability. De-duplicates in-flight calls; a completed
   * probe can be re-run (e.g. on org switch) by calling again.
   */
  probe({ commit }) {
    if (inflightProbe) return inflightProbe
    const gen = probeGen
    commit('SET_PROBING', true)
    const me = (async () => {
      try {
        const list = await devEnvironmentsApi.list({ include_terminal: 1 })
        if (gen !== probeGen) return 'unknown' // reset() ran meanwhile — discard
        commit('SET_ENVS', list)
        commit('SET_LOADED', true)
        commit('SET_LIST_ERROR', null)
        commit('SET_CAPABILITY', 'available')
        return 'available'
      } catch (err) {
        if (gen !== probeGen) return 'unknown'
        const cap = classifyGate(err)
        commit('SET_CAPABILITY', cap)
        return cap
      } finally {
        if (gen === probeGen) commit('SET_PROBING', false)
        // Only release the slot if it is still ours — a reset() may have
        // cleared it and a newer probe may already be in flight.
        if (inflightProbe === me) inflightProbe = null
      }
    })()
    inflightProbe = me
    return me
  },

  /**
   * (Re)load the list. `silent` skips the loading flag (poll ticks) so the
   * table does not flicker. A gate-class error also updates the capability so
   * a flag flipped off mid-session drops the nav entry.
   */
  async load({ commit }, { silent = false } = {}) {
    if (!silent) commit('SET_LIST_LOADING', true)
    try {
      const list = await devEnvironmentsApi.list({ include_terminal: 1 })
      commit('SET_ENVS', list)
      commit('SET_LOADED', true)
      commit('SET_LIST_ERROR', null)
      if (!silent) commit('SET_CAPABILITY', 'available')
      return list
    } catch (err) {
      const cap = classifyGate(err)
      if (cap !== 'error') commit('SET_CAPABILITY', cap)
      commit('SET_LIST_ERROR', errorCopy(err))
      throw err
    } finally {
      if (!silent) commit('SET_LIST_LOADING', false)
    }
  },

  /** Arm the poll chain if a row is transitional and no chain is running. */
  startPoll({ state, commit, dispatch }) {
    if (state.pollHandle) return
    if (!needsListPoll(state.rows)) return
    const tick = async () => {
      commit('SET_POLL_HANDLE', null)
      try {
        await dispatch('load', { silent: true })
      } catch (_) {
        // A failing poll must not kill the chain; errorCopy is already on
        // listError for the view to surface.
      }
      if (needsListPoll(state.rows)) {
        const delay = nextPollDelay(state.pollDelay)
        commit('SET_POLL_DELAY', delay)
        commit('SET_POLL_HANDLE', setTimeout(tick, delay))
      } else {
        commit('SET_POLL_DELAY', 0)
      }
    }
    const delay = nextPollDelay(state.pollDelay)
    commit('SET_POLL_DELAY', delay)
    commit('SET_POLL_HANDLE', setTimeout(tick, delay))
  },

  stopPoll({ state, commit }) {
    if (state.pollHandle) clearTimeout(state.pollHandle)
    commit('SET_POLL_HANDLE', null)
    commit('SET_POLL_DELAY', 0)
  },

  /** After a mutation: restart the backoff from 3 s and (re)arm the chain. */
  ensurePoll({ state, commit, dispatch }) {
    if (!needsListPoll(state.rows)) return
    if (state.pollHandle) clearTimeout(state.pollHandle)
    commit('SET_POLL_HANDLE', null)
    commit('SET_POLL_DELAY', 0)
    dispatch('startPoll')
  },

  /**
   * `POST /dev-environments` → `{ status, data }`. 201 = created/adopted,
   * 200 = an existing env came back. The one-time `dev_workspace.password` in
   * `data` is returned to the caller for immediate display and is NOT stored.
   */
  async create({ commit, dispatch }, body) {
    const key = body?.target?.instance_id ?? 'new'
    commit('SET_BUSY', { id: key, action: 'create', busy: true })
    try {
      const result = await devEnvironmentsApi.create(body)
      if (result?.data?.environment) commit('UPSERT_ENV', result.data.environment)
      commit('SET_CAPABILITY', 'available')
      dispatch('ensurePoll')
      return result
    } finally {
      commit('SET_BUSY', { id: key, action: 'create', busy: false })
    }
  },

  /** `POST /<id>/start` → `{ environment, dev_workspace }` (password not stored). */
  async start({ commit, dispatch }, { id, enable } = {}) {
    commit('SET_BUSY', { id, action: 'start', busy: true })
    try {
      const data = await devEnvironmentsApi.start(id, enable)
      if (data?.environment) commit('UPSERT_ENV', data.environment)
      dispatch('ensurePoll')
      return data
    } finally {
      commit('SET_BUSY', { id, action: 'start', busy: false })
    }
  },

  /** `POST /<id>/stop` → `{ environment, status }`. */
  async stop({ commit, dispatch }, id) {
    commit('SET_BUSY', { id, action: 'stop', busy: true })
    try {
      const data = await devEnvironmentsApi.stop(id)
      if (data?.environment) commit('UPSERT_ENV', data.environment)
      dispatch('ensurePoll')
      return data
    } finally {
      commit('SET_BUSY', { id, action: 'stop', busy: false })
    }
  },

  /** `DELETE /<id>` → `{ deleted, stopped_first, environment_id }`. */
  async remove({ commit }, id) {
    commit('SET_BUSY', { id, action: 'remove', busy: true })
    try {
      const data = await devEnvironmentsApi.remove(id)
      commit('REMOVE_ENV', data?.environment_id ?? id)
      return data
    } finally {
      commit('SET_BUSY', { id, action: 'remove', busy: false })
    }
  },

  /** `GET /<id>?live=1` → refresh one row (detail page mount / manual refresh). */
  async refreshOne({ commit, dispatch }, id) {
    const env = await devEnvironmentsApi.get(id, { live: 1 })
    if (env) commit('UPSERT_ENV', env)
    dispatch('ensurePoll')
    return env
  },

  // ── Developer loop Stage A — git inside the workspace ──────────────────────

  /**
   * `GET /<id>/git/status`. Never throws to the view: on failure the
   * classified copy lands on `gitError[id]` and the stale status (if any) is
   * kept so the card can show the last-known tree behind the banner.
   * Returns the payload or null.
   */
  async loadGitStatus({ commit }, id) {
    commit('SET_GIT_BUSY', { id, op: 'status' })
    try {
      const status = await devEnvironmentsApi.gitStatus(id)
      commit('SET_GIT_STATUS', { id, status: status || null })
      commit('SET_GIT_ERROR', { id, error: null })
      return status || null
    } catch (err) {
      commit('SET_GIT_ERROR', { id, error: gitErrorCopy(err) })
      return null
    } finally {
      commit('SET_GIT_BUSY', { id, op: null })
    }
  },

  /**
   * `GET /<id>/git/diff?path=` → stores `{ path, diff, stat, truncated }` on
   * `gitDiff[id]`. Throws — the card renders diff failures inline next to the
   * file, not on the card-level banner.
   */
  async loadGitDiff({ commit }, { id, path } = {}) {
    commit('SET_GIT_BUSY', { id, op: 'diff' })
    try {
      const data = await devEnvironmentsApi.gitDiff(id, path)
      const diff = {
        path: (data && data.path) || path || null,
        diff: (data && typeof data.diff === 'string') ? data.diff : '',
        stat: (data && typeof data.stat === 'string') ? data.stat : '',
        truncated: Boolean(data && data.truncated)
      }
      commit('SET_GIT_DIFF', { id, diff })
      return diff
    } finally {
      commit('SET_GIT_BUSY', { id, op: null })
    }
  },

  /** Drop the cached diff for an env (collapse / file change). */
  clearGitDiff({ commit }, id) {
    commit('SET_GIT_DIFF', { id, diff: null })
  },

  /**
   * `POST /<id>/git/commit-push`. Returns the payload
   * `{ committed, pushed, sha, short_sha, branch, console, identity }`; on
   * success refreshes the git status AND the env (`refreshOne`) so the
   * timeline picks up the `git_push` audit event. Throws on failure after
   * parking `gitErrorCopy(err)` on `gitError[id]` so the card banner and the
   * caller agree.
   */
  async commitPush({ commit, dispatch }, { id, message, paths, branch, add_all } = {}) {
    commit('SET_GIT_BUSY', { id, op: 'push' })
    commit('SET_GIT_ERROR', { id, error: null })
    try {
      const body = { message }
      if (Array.isArray(paths) && paths.length) body.paths = paths
      if (typeof add_all === 'boolean') body.add_all = add_all
      if (branch) body.branch = branch
      const result = await devEnvironmentsApi.gitCommitPush(id, body)
      commit('SET_GIT_DIFF', { id, diff: null })
      commit('SET_GIT_BUSY', { id, op: null })
      // Refresh after the push — failures here must not turn a successful
      // push into an error for the caller.
      await Promise.allSettled([
        dispatch('loadGitStatus', id),
        dispatch('refreshOne', id)
      ])
      return result
    } catch (err) {
      commit('SET_GIT_ERROR', { id, error: gitErrorCopy(err) })
      throw err
    } finally {
      commit('SET_GIT_BUSY', { id, op: null })
    }
  },

  // ── Developer loop Stage B — release status + catalog adopt/finalise ───────

  /**
   * `GET /<id>/release/status?sha=`. Never throws to the view: on failure the
   * classified copy lands on `releaseError[id]` (the stale status, if any, is
   * kept behind the banner). `nothing_pushed` is parked as the quiet empty
   * state AND clears any stale status. Returns `{ status, err }` so the poll
   * tick can tell a transient failure from a final one.
   */
  async loadReleaseStatus({ commit }, { id, sha } = {}) {
    commit('SET_RELEASE_BUSY', { id, op: 'status' })
    try {
      const status = await devEnvironmentsApi.releaseStatus(id, sha)
      commit('SET_RELEASE', { id, status: status || null })
      commit('SET_RELEASE_ERROR', { id, error: null })
      return { status: status || null, err: null }
    } catch (err) {
      const copy = releaseErrorCopy(err)
      commit('SET_RELEASE_ERROR', { id, error: copy })
      if (copy.code === 'nothing_pushed') commit('SET_RELEASE', { id, status: null })
      return { status: null, err }
    } finally {
      commit('SET_RELEASE_BUSY', { id, op: null })
    }
  },

  /**
   * Arm (or re-arm from 6 s) the release poll for one env. Loads immediately,
   * then chains ticks at `releasePollDelay` until `releasePollShouldStop`
   * (deploy settled AND pipeline settled), a final error (409 nothing_pushed,
   * 4xx), or `RELEASE_POLL_MAX_MS`. The same rule governs the mount-time and
   * refresh polls — they all come through here.
   * Idempotent per env: calling again restarts the back-off and the clock
   * (a fresh push deserves a fresh 10 minutes).
   */
  startReleasePoll({ commit, dispatch }, { id, sha } = {}) {
    if (id == null || id === '') return
    const key = String(id)
    clearReleasePoll(key)
    releasePolls[key] = { handle: null, delay: 0, startedAt: Date.now(), sha: sha || undefined }
    commit('SET_RELEASE_POLLING', { id: key, on: true })

    const tick = async () => {
      const p = releasePolls[key]
      if (!p) return
      p.handle = null
      const { status, err } = await dispatch('loadReleaseStatus', { id: key, sha: p.sha })
      if (!releasePolls[key]) return // stopped meanwhile
      if (err ? releaseErrorIsFinal(err) : releasePollShouldStop(status)) {
        dispatch('stopReleasePoll', key)
        return
      }
      if (Date.now() - p.startedAt >= RELEASE_POLL_MAX_MS) {
        dispatch('stopReleasePoll', key)
        return
      }
      p.delay = releasePollDelay(p.delay)
      p.handle = setTimeout(tick, p.delay)
    }
    tick()
  },

  stopReleasePoll({ commit }, id) {
    if (id == null || id === '') return
    clearReleasePoll(id)
    commit('SET_RELEASE_POLLING', { id, on: false })
  },

  /**
   * `POST /<id>/release/adopt` `{ new_tag?, note? }` (catalog only). On
   * success refreshes the env (the `release_build_triggered` audit event) and
   * arms the poll so the pending build's progress shows. Throws after parking
   * `releaseErrorCopy(err)` on `releaseError[id]`.
   */
  async releaseAdopt({ commit, dispatch }, { id, new_tag, note } = {}) {
    commit('SET_RELEASE_BUSY', { id, op: 'adopt' })
    commit('SET_RELEASE_ERROR', { id, error: null })
    try {
      const result = await devEnvironmentsApi.releaseAdopt(id, { new_tag, note })
      commit('SET_RELEASE_BUSY', { id, op: null })
      await Promise.allSettled([dispatch('refreshOne', id)])
      dispatch('startReleasePoll', { id })
      return result
    } catch (err) {
      commit('SET_RELEASE_ERROR', { id, error: releaseErrorCopy(err) })
      throw err
    } finally {
      commit('SET_RELEASE_BUSY', { id, op: null })
    }
  },

  /**
   * `POST /<id>/release/finalize` `{ adopt, exit_dev_mode, new_tag?, note?,
   * pipeline_id? }` (catalog only). Adopts `data.environment` into the list
   * (with `exit_dev_mode` the record moves to stopped) and re-arms the poll
   * so the rollout is watched. Throws after parking the copy.
   */
  async releaseFinalize({ commit, dispatch }, { id, adopt = true, exit_dev_mode = false, new_tag, note, pipeline_id } = {}) {
    commit('SET_RELEASE_BUSY', { id, op: 'finalize' })
    commit('SET_RELEASE_ERROR', { id, error: null })
    try {
      const result = await devEnvironmentsApi.releaseFinalize(id, { adopt, exit_dev_mode, new_tag, note, pipeline_id })
      if (result && result.environment) commit('UPSERT_ENV', result.environment)
      commit('SET_RELEASE_BUSY', { id, op: null })
      dispatch('ensurePoll')
      dispatch('startReleasePoll', { id })
      return result
    } catch (err) {
      commit('SET_RELEASE_ERROR', { id, error: releaseErrorCopy(err) })
      throw err
    } finally {
      commit('SET_RELEASE_BUSY', { id, op: null })
    }
  },

  // ── The APP's connection info (installed-app builder, on demand) ───────────

  /**
   * `GET /<id>/connection-info` → stores `{ info, meta, loadedAt }` on
   * `connectionInfo[id]`. Never throws to the view: on failure `errorCopy(err)`
   * lands on `connectionInfoError[id]` (a stale payload, if any, is kept behind
   * the banner). Returns the stored value or null. Never logs the payload.
   *
   * Accepts a bare id (the normal, masked read) or `{ id, reveal: true }` — the
   * audited ask for clear values. A reveal that fails leaves the masked payload
   * exactly where it is and parks `revealErrorCopy(err)` on
   * `connectionInfoRevealError[id]`, so the customer keeps a working modal.
   */
  async loadConnectionInfo({ commit }, arg) {
    const opts = arg && typeof arg === 'object' ? arg : { id: arg }
    const id = opts.id
    const reveal = opts.reveal === true
    if (id == null || id === '') return null
    commit('SET_CONNECTION_INFO_BUSY', { id, busy: true })
    try {
      const { info, meta } = await devEnvironmentsApi.connectionInfo(id, { reveal })
      const value = { info: info || {}, meta: meta || null, loadedAt: Date.now(), revealRequested: reveal }
      commit('SET_CONNECTION_INFO', { id, value })
      commit('SET_CONNECTION_INFO_ERROR', { id, error: null })
      commit('SET_CONNECTION_INFO_REVEAL_ERROR', { id, error: null })
      return value
    } catch (err) {
      if (reveal) commit('SET_CONNECTION_INFO_REVEAL_ERROR', { id, error: revealErrorCopy(err) })
      else commit('SET_CONNECTION_INFO_ERROR', { id, error: errorCopy(err) })
      return null
    } finally {
      commit('SET_CONNECTION_INFO_BUSY', { id, busy: false })
    }
  },

  /** Drop a cached connection-info payload (modal closed / env stopped). */
  clearConnectionInfo({ commit }, id) {
    commit('SET_CONNECTION_INFO', { id, value: null })
    commit('SET_CONNECTION_INFO_ERROR', { id, error: null })
    commit('SET_CONNECTION_INFO_REVEAL_ERROR', { id, error: null })
  },

  /** Logout / org switch: drop everything, stop the poll, invalidate any
   *  in-flight probe so the next probe() starts fresh for the new identity. */
  reset({ commit, dispatch }) {
    dispatch('stopPoll')
    Object.keys(releasePolls).forEach(clearReleasePoll)
    probeGen += 1
    inflightProbe = null
    commit('RESET')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
